import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'

import { SecondaryButton } from 'components/Shared/Button'
import { ButtonGroup } from 'react-bootstrap'

import integrationsApi from 'api/integrations'
import { Table, Spinner } from 'react-bootstrap'
import Paginator from 'components/Shared/Paginator'
import { numberWithThousandsSeparators } from 'utils/helpers'
import { ATTRIBUTION_MODELS } from 'domain/events/attributionModel/eventAttributionModelReducer'
import { useAttributionModel } from 'domain/events/attributionModel/EventAttributionModelContextProvider'

const columnStyles = {
  name: { minWidth: 200 },
  ad_spend: { minWidth: 120 },
  clicks: { minWidth: 150 },
  roi: { },
}

const mapData = (data, mode) => {
  if (!data || !data.items) return data
  if (mode === 'ads') {
    return data
  }
  const { items } = data
  return {
    ...data,
    items: items.map((item) => ({
      ...item,
      name: item.name.split('_').pop().replace(/-/g, ' '),
    })),
  }
}

export default function InsightsBreakdown({ eventId }) {
  const { t } = useTranslation()
  const { attributionModel } = useAttributionModel()
  const [mode, setMode] = useState('adsets')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState({ items: [], total: 0, limit: 20, page: 1, pages: 1 })

  const [sortBy, setSortBy] = useState(null)
  const [sortOrder, setSortOrder] = useState(null) // 'asc' | 'desc' | null

  const [page, setPage] = useState(1)
  const pageSize = 20

  useEffect(() => {
    let isMounted = true
    setLoading(true)
    setError(null)
    integrationsApi
      .fetchInsightsBreakdown(eventId, mode, {
        page,
        limit: pageSize,
        sortBy,
        sortOrder,
      })
      .then((res) => {
        if (isMounted) {
          const data = mapData(res, mode)
          setData(data)
        }
      })
      .catch(() => {
        if (isMounted) setError(t('Dashboard.insightsBreakdown.error') || 'Failed to load insights')
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })
    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, mode, page, sortBy, sortOrder])

  const handleModeChange = (newMode) => {
    if (mode !== newMode) {
      setMode(newMode)
      setPage(1)
      setSortBy(null)
      setSortOrder(null)
    }
  }

  // Sorting logic for columns
  const sortableColumns = [
    { key: 'spend', label: t('Dashboard.insightsBreakdown.table.ad_spend') },
    { key: 'impressions', label: t('Dashboard.insightsBreakdown.table.impressions') },
    { key: 'clicks', label: t('Dashboard.insightsBreakdown.table.clicks') },
    { key: 'reach', label: t('Dashboard.insightsBreakdown.table.reach') },
    { key: 'ctr', label: t('Dashboard.insightsBreakdown.table.ctr') },
  ]

  // Only show these metrics for Meta attribution model
  const metaOnlyColumns = [
    { key: 'purchases', label: t('Dashboard.insightsBreakdown.table.purchase') },
    { key: 'revenue', label: t('Dashboard.insightsBreakdown.table.revenue') },
    { key: 'cpo', label: t('Dashboard.insightsBreakdown.table.cpo') },
    { key: 'roi', label: t('Dashboard.insightsBreakdown.table.roi') },
  ]

  const handleSortClick = (colKey) => {
    if (sortBy !== colKey) {
      setSortBy(colKey)
      setSortOrder('desc')
      setPage(1)
    } else if (sortOrder === 'desc') {
      setSortOrder('asc')
      setPage(1)
    } else if (sortOrder === 'asc') {
      setSortBy(null)
      setSortOrder(null)
      setPage(1)
    }
  }

  return (
    <div className="insights-breakdown event-series-stats card">
      <div
        className="d-flex justify-content-between w-100 card-header"
        style={{ background: 'none' }}
      >
        <div className="insights-breakdown_header">
          <h2 className="card-title-default d-inline-block mb-0">
            {t('Dashboard.insightsBreakdown.title')}
          </h2>
          <div className="insights-breakdown_mode">
            <ButtonGroup>
              <SecondaryButton
                color="navy"
                disabled={loading}
                classNames={cn({ active: mode === 'adsets' })}
                onClick={() => handleModeChange('adsets')}
                text={t('Dashboard.insightsBreakdown.modes.tasteClusters')}
                size="small"
              />
              <SecondaryButton
                color="navy"
                disabled={loading}
                classNames={cn({ active: mode === 'ads' })}
                onClick={() => handleModeChange('ads')}
                text={t('Dashboard.insightsBreakdown.modes.ads')}
                size="small"
              />
            </ButtonGroup>
          </div>
        </div>
      </div>
      <div className="card-body">
        {loading && !data.items.length && (
          <div className="d-flex justify-content-center align-items-center my-4">
            <Spinner animation="border" size="md" />
          </div>
        )}
        {error && <p className="alert alert-danger">{t('common.genericError')}</p>}
        {!error && (
          <>
            {data.items.length === 0 ? (
              <div className="sentinel_results__empty-msg">
                {t('Dashboard.insightsBreakdown.emptyMsg')}
              </div>
            ) : (
              <Table responsive className="mb-0">
                <thead>
                  <tr>
                    <th style={columnStyles.name} className="pl-0">
                      {t(`Dashboard.insightsBreakdown.table.name.${mode}`)}
                    </th>
                    <th style={columnStyles.status}>
                      {t('Dashboard.insightsBreakdown.table.status')}
                    </th>
                    {sortableColumns.map((col, idx) => {
                      let isActive = sortBy === col.key
                      let isAsc = isActive && sortOrder === 'asc'
                      let isDesc = isActive && sortOrder === 'desc'
                      const isLast = idx === sortableColumns.length - 1
                      return (
                        <th
                          key={col.key}
                          className={`text-right sort-th ${attributionModel !== ATTRIBUTION_MODELS.meta && isLast ? 'pr-0' : ''}`}
                          style={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            position: 'relative',
                            ...(columnStyles[col.key] || {}),
                          }}
                          onClick={() => handleSortClick(col.key)}
                        >
                          <span className="d-inline-flex align-items-center sort-header-label">
                            <span
                              className={cn('sort-arrow-wrapper', {
                                active: isActive,
                                asc: isAsc,
                                desc: isDesc,
                              })}
                              style={{
                                marginLeft: 4,
                                display: 'inline-flex',
                                alignItems: 'center',
                              }}
                            >
                              <span className="sort-arrow-icon">
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 12 12"
                                  style={{
                                    opacity: isActive ? 1 : 0.5,
                                    transform: isAsc ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.15s',
                                    fill: isActive ? '#22336a' : '#b0b0b0',
                                    display: 'inline',
                                  }}
                                >
                                  <path d="M6 3l4 5H2z" />
                                </svg>
                              </span>
                            </span>
                            {col.label}
                          </span>
                        </th>
                      )
                    })}
                    {/* Show Meta-only columns if attribution model is meta */}
                    {attributionModel === ATTRIBUTION_MODELS.meta &&
                      metaOnlyColumns.map((col, idx) => (
                        <th
                          key={col.key}
                          className={`text-right ${idx === metaOnlyColumns.length - 1 ? 'pr-0' : ''}`}
                          style={columnStyles[col.key] || {}}
                        >
                          {col.label}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item, idx) => (
                    <tr key={idx}>
                      <td style={columnStyles.name} className="pl-0">{item.name}</td>
                      <td style={columnStyles.status}>{item.status?.toLowerCase()}</td>
                      <td className="text-right" style={columnStyles.ad_spend}>
                        {numberWithThousandsSeparators(item.spend)}
                      </td>
                      <td className="text-right" style={columnStyles.impressions}>
                        {numberWithThousandsSeparators(item.impressions)}
                      </td>
                      <td className="text-right" style={columnStyles.clicks}>
                        {numberWithThousandsSeparators(item.clicks)}
                      </td>
                      <td className="text-right" style={columnStyles.reach}>
                        {numberWithThousandsSeparators(item.reach)}
                      </td>
                      <td className={`text-right ${attributionModel !== ATTRIBUTION_MODELS.meta ? 'pr-0' : ''}`} style={columnStyles.ctr}>
                        {numberWithThousandsSeparators(item.ctr, { maxFractionDigits: 2, suffix: '%' })}
                      </td>
                      {/* Show Meta-only values if attribution model is meta */}
                      {attributionModel === ATTRIBUTION_MODELS.meta && (
                        <>
                          <td className="text-right" style={columnStyles.purchases}>
                            {numberWithThousandsSeparators(item.purchase)}
                          </td>
                          <td className="text-right" style={columnStyles.revenue}>
                            {numberWithThousandsSeparators(item.revenue)}
                          </td>
                          <td className="text-right" style={columnStyles.cpo}>
                            {numberWithThousandsSeparators(item.cpo)}
                          </td>
                          <td className="text-right pr-0" style={columnStyles.roi}>
                            {numberWithThousandsSeparators(item.roi, { suffix: '%' })}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <Paginator
              onPageClicked={setPage}
              page={data.page}
              totalPages={data.pages}
              pageSize={data.limit}
              totalItems={data.total}
              hideWhenSinglePage
              classNames="mt-0"
            />
          </>
        )}
      </div>
    </div>
  )
}

InsightsBreakdown.propTypes = {
  eventId: PropTypes.string.isRequired,
}
